import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, toAbsoluteAssetUrl, postMultipart } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { portalBasePath } from "../lib/portalPaths";
import { toast } from "sonner";
import { StatusPill } from "../components/StatusPill";
import { DatePickerField } from "../components/DatePickerField";
import { MotionSection } from "../components/Motion";
import { motion, useReducedMotion } from "framer-motion";
import { Archive, ArchiveRestore, ArrowUpDown, ChevronUp, ChevronDown, ArrowLeftRight, Trash2, ArrowRight, X } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  OFFERING_PHASES,
  OFFERING_KEYS,
  OFFERING_LABEL,
  OFFERING_LABEL_SHORT,
  OFFERING_COLORS,
  canonicalOffering,
  offeringColor,
} from "../config/offeringPhases";
import { formatCycleRangeLabel } from "../lib/cycleLabels";
import {
  STAGE_LABEL,
  ENGAGEMENT_STAGE_OPTIONS,
  compareClientsByProjectDateDesc,
  isEngagementBucketStage,
  normalizeEngagementStage,
} from "../lib/engagementStages";

const STAGE_ACCENT = {
  active: "#001EFF",
  closeout: "#5B6B8C",
  archive: "#B0B6C2",
};

const phaseLabelForClient = (client, phaseKey) => {
  if (!phaseKey) return "—";
  const offering = canonicalOffering(client?.service_tier);
  const phases = OFFERING_PHASES[offering] || OFFERING_PHASES.web_design;
  const row = (phases || []).find((p) => p.key === phaseKey);
  return row?.label || phaseKey;
};

/** Aligns legacy tier strings with current engagement types (matches backend LEGACY_TIER_ALIAS). */
const ENGAGEMENT_FROM_STORED = { essentials: "brand_design", growth: "web_design", partnership: "operational_ecosystem", infrastructure: "operational_ecosystem" };

function canonicalEngagement(raw) {
  return ENGAGEMENT_FROM_STORED[raw] || raw;
}

function formatLastArtifact(client) {
  const items = Array.isArray(client?.artifacts_sent) ? client.artifacts_sent : [];
  if (!items.length) return "—";
  const last = items[items.length - 1];
  const when = last?.sent_at
    ? new Date(last.sent_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";
  const channel = last?.channel === "download" ? "downloaded" : "emailed";
  const name = last?.invoice_number || last?.file_name || "packet";
  return when ? `${name} · ${channel} ${when}` : `${name} · ${channel}`;
}

const MODAL_TIMELINE_PANEL_CLASS =
  "mt-3 w-full overflow-hidden rounded-[var(--t-event-pill-radius)] border border-[var(--t-border)] p-0 text-[var(--t-text)] t-glass";

const parseDateValue = (value) => {
  if (!value) return undefined;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
};

const toDateValue = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isoToFormDate = (iso) => {
  if (!iso) return "";
  const parsed = parseDateValue(iso);
  if (!parsed || Number.isNaN(parsed.getTime())) return "";
  return toDateValue(parsed);
};

const toTimelinePayload = (value) => (value ? String(value).slice(0, 10) : null);

function formatClientTimelineLabel(client) {
  if (client?.engagement_type === "monthly" && client?.active_cycle) {
    return formatCycleRangeLabel(client.active_cycle) || "—";
  }
  const from = client.timeline_start ? new Date(client.timeline_start) : undefined;
  const to = client.timeline_end ? new Date(client.timeline_end) : undefined;
  if (!from && !to) return "—";
  const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
  const fromLabel = from ? fmt(from) : null;
  const toLabel = to ? fmt(to) : null;
  if (fromLabel && !toLabel) return fromLabel;
  if (!fromLabel && toLabel) return toLabel;
  return `${fromLabel || ""} – ${toLabel || ""}`.trim();
}

function tierColor(client) {
  return offeringColor(client?.service_tier);
}

function parseDue(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function daysUntil(due) {
  if (!due) return null;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  return Math.round((end.getTime() - start.getTime()) / 86400000);
}

function formatDue(iso) {
  const d = parseDue(iso);
  if (!d) return "—";
  const n = daysUntil(d);
  const label = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (n === null) return label;
  if (n < 0) return `${label} · overdue`;
  if (n === 0) return `${label} · today`;
  if (n === 1) return `${label} · tomorrow`;
  if (n <= 7) return `${label} · ${n}d`;
  return label;
}

function attentionScore(c) {
  let score = 0;
  score += (c.open_approvals || 0) * 10;
  score += (c.unread_messages || 0) * 6;
  const n = daysUntil(parseDue(c.next_milestone?.due_date));
  if (n !== null) {
    if (n < 0) score += 12;
    else if (n === 0) score += 9;
    else if (n <= 3) score += 5;
    else if (n <= 7) score += 2;
  }
  return score;
}

function attentionBits(c) {
  const bits = [];
  if (c.open_approvals) bits.push(`${c.open_approvals} approval${c.open_approvals === 1 ? "" : "s"}`);
  if (c.unread_messages) bits.push(`${c.unread_messages} msg`);
  if (c.next_milestone?.due_date) {
    const n = daysUntil(parseDue(c.next_milestone.due_date));
    if (n !== null && n <= 7) bits.push(formatDue(c.next_milestone.due_date));
  }
  return bits;
}

function needsAttention(c) {
  return (
    normalizeEngagementStage(c.engagement_stage) === "active" &&
    (c.status || (c.archived ? "archived" : "active")) === "active" &&
    attentionScore(c) > 0
  );
}

export default function AdminDashboard() {
  const { switchClient } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const reduce = useReducedMotion();
  const stageParam = ["active", "closeout", "archive"].includes(params.get("stage"))
    ? params.get("stage")
    : "active";
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingClient, setEditingClient] = useState(null);

  const load = async ({ showLoading = true } = {}) => {
    if (showLoading) setLoading(true);
    try {
      const { data } = await api.get("/admin/clients");
      setClients(data);
      setEditingClient((prev) => {
        if (!prev) return null;
        const next = (data || []).find((c) => c.id === prev.id);
        return next !== undefined ? next : prev;
      });
    } catch (e) { toast.error("Could not load engagements"); }
    finally { if (showLoading) setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const syncClient = (updated) => {
    if (!updated?.id) return;
    setClients((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
    setEditingClient((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
  };

  const enterClient = (c) => {
    const stage = normalizeEngagementStage(c.engagement_stage);
    if (!isEngagementBucketStage(stage)) {
      toast.message("Workspace opens after a prospect becomes an engagement.");
      navigate(`/admin/outreach/${c.id}`);
      return;
    }
    if (stage === "archive") {
      toast.message("Archived engagements don’t open a workspace.");
      return;
    }
    switchClient(c);
    navigate(portalBasePath(c));
  };

  const setStatus = async (c, status) => {
    await api.patch(`/admin/clients/${c.id}/status`, { status });
    toast.success(`Status: ${status}`);
    load();
  };

  const inviteToHome = async (c) => {
    try {
      await api.post(`/admin/clients/${c.id}/invite`);
      toast.success("Workspace invite sent");
      load({ showLoading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send workspace invite");
    }
  };

  const setEngagementStage = async (c, engagement_stage) => {
    try {
      await api.patch(`/admin/clients/${c.id}/engagement-stage`, { engagement_stage });
      toast.success(`Stage: ${STAGE_LABEL[engagement_stage] || engagement_stage}`);
      load({ showLoading: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update stage");
    }
  };

  const setEngagementType = async (c, engagement_type) => {
    try {
      await api.patch(`/admin/clients/${c.id}/engagement-type`, { engagement_type });
      toast.success(`Engagement: ${engagement_type}`);
      load();
    } catch {
      toast.error("Could not update engagement");
    }
  };

  const setServiceTier = async (c, service_tier) => {
    try {
      await api.patch(`/admin/clients/${c.id}/service-tier`, { service_tier });
      toast.success("Type updated");
      load();
    } catch {
      toast.error("Could not update type");
    }
  };

  const boardClients = useMemo(
    () => clients.filter((c) => isEngagementBucketStage(c.engagement_stage)),
    [clients],
  );

  const byStage = useMemo(() => {
    const map = { active: [], closeout: [], archive: [] };
    boardClients.forEach((c) => {
      const s = normalizeEngagementStage(c.engagement_stage);
      if (map[s]) map[s].push(c);
    });
    Object.values(map).forEach((list) => list.sort(compareClientsByProjectDateDesc));
    return map;
  }, [boardClients]);

  const counts = useMemo(
    () =>
      Object.fromEntries(
        ENGAGEMENT_STAGE_OPTIONS.map(([key]) => [key, byStage[key]?.length || 0]),
      ),
    [byStage],
  );

  const rows = byStage[stageParam] || [];

  const attention = useMemo(
    () =>
      boardClients
        .filter(needsAttention)
        .sort((a, b) => attentionScore(b) - attentionScore(a))
        .slice(0, 8),
    [boardClients],
  );

  const typeMix = useMemo(() => {
    const active = boardClients.filter(
      (c) =>
        normalizeEngagementStage(c.engagement_stage) === "active" &&
        (c.status || (c.archived ? "archived" : "active")) !== "archived",
    );
    const buckets = OFFERING_KEYS.map((key) => ({
      key,
      label: OFFERING_LABEL_SHORT[key],
      color: OFFERING_COLORS[key],
      count: active.filter((c) => canonicalOffering(c.service_tier) === key).length,
    }));
    const total = buckets.reduce((sum, b) => sum + b.count, 0);
    return { buckets, total };
  }, [boardClients]);

  const setStageFilter = (stage) => {
    const next = new URLSearchParams(params);
    next.set("stage", stage);
    setParams(next, { replace: true });
  };

  const maxType = Math.max(1, ...typeMix.buckets.map((b) => b.count));

  return (
    <div className="min-h-0 max-w-full overflow-x-clip p-4 sm:p-5 md:p-7" data-testid="admin-dashboard">
      <MotionSection
        as="header"
        className="mb-5 flex flex-col gap-4 sm:mb-6 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <div className="label-eyebrow mb-2">Studio</div>
        </div>
      </MotionSection>

      <div className="mb-5 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <section className="neo-panel p-5" data-testid="engagements-needs-you">
          <div className="mb-3 flex items-end justify-between gap-3">
            <div>
              <div className="label-eyebrow mb-1">Needs you</div>
              <div className="font-heading text-xl tracking-tight !font-light">Attention</div>
            </div>
            <div className="font-heading text-xl tracking-tight !font-light text-[var(--t-accent)]">
              {loading ? "—" : attention.length}
            </div>
          </div>
          {loading ? (
            <p className="text-sm text-[var(--t-meta)]">Loading…</p>
          ) : attention.length === 0 ? (
            <p className="text-sm text-[var(--t-meta)]">Nothing waiting.</p>
          ) : (
            <p className="text-sm leading-relaxed text-[var(--t-text)]/80">
              {attention.map((c, i) => (
                <span key={c.id}>
                  {i > 0 ? <span className="text-[var(--t-meta)]"> · </span> : null}
                  <button
                    type="button"
                    onClick={() => enterClient(c)}
                    className="hover:text-[var(--t-accent)]"
                    data-testid={`needs-you-${c.id}`}
                  >
                    {c.name}
                  </button>
                  <span className="text-[var(--t-meta)]">
                    {" "}
                    ({attentionBits(c).join(", ") || "attention"})
                  </span>
                </span>
              ))}
            </p>
          )}
        </section>

        <section className="neo-panel p-5" data-testid="engagements-type-mix">
          <div className="mb-3 flex items-end justify-between gap-3">
            <div>
              <div className="label-eyebrow mb-1">Active mix</div>
              <div className="font-heading text-xl tracking-tight !font-light">Engagement kinds</div>
            </div>
            <div className="font-heading text-xl tracking-tight !font-light text-[var(--t-accent)]">
              {typeMix.total}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {typeMix.buckets.map((row, idx) => {
              const width = row.count === 0 ? 10 : Math.max(22, (row.count / maxType) * 100);
              return (
                <div key={row.key} className="text-left">
                  <div className="mb-1 flex items-center justify-between gap-2 text-[11px] uppercase tracking-[0.14em]">
                    <span className="text-[var(--t-meta)]">
                      {idx + 1}. {row.label}
                    </span>
                    <span className="tabular-nums text-[var(--t-text)]">{row.count}</span>
                  </div>
                  <div className="neo-well h-2 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: row.color }}
                      initial={reduce ? false : { width: 0 }}
                      animate={{ width: `${width}%` }}
                      transition={{ duration: 0.55, delay: idx * 0.05 }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {loading ? (
        <p className="py-16 text-center text-sm text-[var(--t-meta)]">Loading…</p>
      ) : (
        <div
          className="grid grid-cols-1 gap-4 lg:grid-cols-[14rem_minmax(0,1fr)] lg:items-start"
          data-testid="engagements-split-rail"
        >
          <aside
            className="neo-panel px-2 pb-2 pt-3 lg:sticky lg:top-4"
            data-testid="engagements-stage-nav"
          >
            <div className="label-eyebrow px-3 pb-2">Stages</div>
            <div className="space-y-1">
              {ENGAGEMENT_STAGE_OPTIONS.map(([key, label]) => {
                const on = stageParam === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStageFilter(key)}
                    className={`flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left ${
                      on
                        ? "bg-[rgba(0,30,255,0.08)] text-[var(--t-accent)]"
                        : "text-[var(--t-meta)] hover:text-[var(--t-text)]"
                    }`}
                    data-testid={`engagements-stage-${key}`}
                  >
                    <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em]">
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: STAGE_ACCENT[key] }}
                      />
                      {label}
                    </span>
                    <span className="font-heading text-lg !font-extralight tabular-nums">
                      {counts[key] || 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="min-w-0 pt-3">
            <div className="mb-3 flex items-end justify-between gap-2">
              <div>
                <div className="label-eyebrow mb-0.5">{STAGE_LABEL[stageParam]}</div>
              </div>
              <span className="text-[11px] text-[var(--t-meta)]">
                {rows.length} {rows.length === 1 ? "engagement" : "engagements"}
              </span>
            </div>
            {rows.length === 0 ? (
              <p className="py-10 text-sm text-[var(--t-meta)]" data-testid="empty-clients">
                Empty stage.
              </p>
            ) : (
              <div className="space-y-4">
                {rows.map((c) => {
                  const stage = normalizeEngagementStage(c.engagement_stage);
                  const bar = tierColor(c);
                  const bits = attentionBits(c);
                  const timeline = formatClientTimelineLabel(c);
                  return (
                    <div
                      key={c.id}
                      className="neo-panel relative overflow-hidden p-4 text-left"
                      data-testid={`client-row-${c.id}`}
                    >
                      <div
                        className="absolute left-0 top-0 h-full w-1.5"
                        style={{ background: bar }}
                      />
                      <div className="flex flex-col gap-3 pl-3 sm:flex-row sm:items-center sm:justify-between">
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => enterClient(c)}
                        >
                          <div className="font-heading text-lg !font-light hover:text-[var(--t-accent)]">
                            {c.name}
                          </div>
                          <div className="mt-1 text-xs text-[var(--t-meta)]">
                            {STAGE_LABEL[stage] || stage} ·{" "}
                            {OFFERING_LABEL_SHORT[canonicalOffering(c.service_tier)] || "—"}
                            {" · "}
                            {phaseLabelForClient(c, c.active_phase)}
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs text-[var(--t-meta)]">
                            <span data-testid={`inline-timeline-${c.id}`}>{timeline}</span>
                            {bits.length ? <span>{bits.join(" · ")}</span> : null}
                          </div>
                        </button>
                        <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
                          <button
                            type="button"
                            onClick={() => setEditingClient(c)}
                            className="hidden text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-text)] md:inline"
                            data-testid={`edit-engagement-${c.id}`}
                          >
                            Edit
                          </button>
                          {stage !== "archive" ? (
                            <button
                              type="button"
                              onClick={() => inviteToHome(c)}
                              className="hidden text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-text)] md:inline"
                              data-testid={`invite-home-${c.id}`}
                              title={
                                c.magic_link_invited_at
                                  ? "Resend workspace Home invite"
                                  : "Invite client to their workspace Home"
                              }
                            >
                              {c.magic_link_invited_at ? "Resend invite" : "Invite to workspace"}
                            </button>
                          ) : null}
                          {stage !== "archive" ? (
                            <button
                              type="button"
                              onClick={() => enterClient(c)}
                              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]"
                              data-testid={`open-workspace-${c.id}`}
                            >
                              Workspace <ArrowRight size={12} />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {editingClient && (
        <CreateClientModal
          client={editingClient}
          onClose={() => setEditingClient(null)}
          onCreated={() => load({ showLoading: false })}
          onClientUpdated={syncClient}
        />
      )}
    </div>
  );
}


const FilterGroup = ({ label, value, onChange, options, testidPrefix }) => (
  <div className="flex items-center gap-3 shrink-0">
    <span className="label-eyebrow">{label}</span>
    <div className="flex w-max flex-nowrap gap-0 pb-px">
      {options.map(([v, lbl]) => {
        const active = value === v;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={`shrink-0 px-3 sm:px-4 py-3 text-[11px] tracking-[0.18em] font-normal border-b-2 -mb-px min-h-[44px] flex items-center gap-2 whitespace-nowrap transition-colors ${
              active ? "border-[var(--t-accent)] text-[var(--t-text)]" : "border-transparent text-[var(--t-meta)] hover:text-[var(--t-text)]"
            }`}
            data-testid={`${testidPrefix}-${v}`}
          >
            {String(lbl).toUpperCase()}
          </button>
        );
      })}
    </div>
  </div>
);

const Th = ({ label, sortKey, sort, setSort, align = "left" }) => {
  const active = sort.key === sortKey;
  const Icon = active ? (sort.dir === "asc" ? ChevronUp : ChevronDown) : ArrowUpDown;
  return (
    <th
      className={`whitespace-nowrap px-4 py-3 text-xs text-[var(--t-meta)] font-normal ${align === "right" ? "text-right" : "text-left"}`}
    >
      <button
        type="button"
        onClick={() => setSort({ key: sortKey, dir: active && sort.dir === "asc" ? "desc" : "asc" })}
        className={`inline-flex min-w-0 max-w-full items-center gap-1 whitespace-nowrap ${active ? "text-[var(--t-text)]" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
        data-testid={`sort-${sortKey}`}
      >
        <span className="min-w-0 truncate">{label}</span>
        <Icon size={12} strokeWidth={1.5} className="shrink-0" />
      </button>
    </th>
  );
};

const RowActions = ({ c, onSetStatus, onSetEngagement, onSetServiceTier, onSetStage, onEdit, onOpenWorkspace, status }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open) return;
    const btn = btnRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;

    const rect = btn.getBoundingClientRect();
    const menuWidth = menu.offsetWidth || 180;
    const menuHeight = menu.offsetHeight;
    const gap = 6;
    const padding = 12;
    const maxHeight = window.innerHeight - padding * 2;

    const left = Math.max(padding, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - padding));

    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - padding) {
      const aboveTop = rect.top - gap - menuHeight;
      if (aboveTop >= padding) {
        top = aboveTop;
      } else {
        top = padding;
      }
    }

    setPos({ top, left });
    menu.style.maxHeight = `${maxHeight}px`;
  }, [open, c.id, status, c.service_tier, c.engagement_type, c.magic_link_invited_at]);

  return (
    <div className="inline-block">
      <button ref={btnRef} onClick={() => setOpen(!open)} className="t-btn-ghost text-xs px-2 py-1" data-testid={`row-menu-${c.id}`}>···</button>
      {open && (
        <>
          <div className="fixed inset-0 z-[200]" onClick={() => setOpen(false)} />
          <div
            ref={menuRef}
            className="fixed z-[210] bg-[var(--t-bg)] border border-[var(--t-border)] min-w-[180px] rounded-[var(--t-event-pill-radius)] overflow-y-auto overscroll-contain"
            style={{ top: pos.top, left: pos.left }}
          >
            <button
              onClick={() => { onEdit?.(c); setOpen(false); }}
              className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-accent)] border-b border-[var(--t-border)]"
              data-testid={`edit-client-${c.id}`}
            >
              Edit engagement
            </button>
            <button
              onClick={() => { onOpenWorkspace?.(); setOpen(false); }}
              className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-accent)] border-b border-[var(--t-border)]"
              data-testid={`open-workspace-${c.id}`}
            >
              Open workspace
            </button>
            {ENGAGEMENT_STAGE_OPTIONS.map(([key, label]) =>
              normalizeEngagementStage(c.engagement_stage) !== key ? (
                <button
                  key={key}
                  onClick={() => { onSetStage?.(key); setOpen(false); }}
                  className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-accent)] border-b border-[var(--t-border)]"
                  data-testid={`set-stage-${key}-${c.id}`}
                >
                  Stage: {label}
                </button>
              ) : null,
            )}
            {c.service_tier !== "brand_design" && <button onClick={() => { onSetServiceTier("brand_design"); setOpen(false); }} className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-accent)] border-b border-[var(--t-border)]" data-testid={`set-type-brand-${c.id}`}>Set type: Brand design</button>}
            {c.service_tier !== "web_design" && <button onClick={() => { onSetServiceTier("web_design"); setOpen(false); }} className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-accent)] border-b border-[var(--t-border)]" data-testid={`set-type-web-${c.id}`}>Set type: Web design</button>}
            {c.service_tier !== "product_design" && <button onClick={() => { onSetServiceTier("product_design"); setOpen(false); }} className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-accent)] border-b border-[var(--t-border)]" data-testid={`set-type-product-${c.id}`}>Set type: Product design</button>}
            {c.service_tier !== "operational_ecosystem" && <button onClick={() => { onSetServiceTier("operational_ecosystem"); setOpen(false); }} className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-accent)] border-b border-[var(--t-border)]" data-testid={`set-type-operational-${c.id}`}>Set type: Operational ecosystem</button>}
            {status !== "active" && <button onClick={() => { onSetStatus("active"); setOpen(false); }} className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-accent)] border-b border-[var(--t-border)]" data-testid={`set-active-${c.id}`}>Set active</button>}
            {c.engagement_type !== "project" && <button onClick={() => { onSetEngagement("project"); setOpen(false); }} className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-accent)] border-b border-[var(--t-border)]" data-testid={`set-project-${c.id}`}>Set project engagement</button>}
            {c.engagement_type !== "monthly" && <button onClick={() => { onSetEngagement("monthly"); setOpen(false); }} className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-accent)] border-b border-[var(--t-border)]" data-testid={`set-monthly-${c.id}`}>Set monthly engagement</button>}
            {status !== "paused" && <button onClick={() => { onSetStatus("paused"); setOpen(false); }} className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-accent)] border-b border-[var(--t-border)]" data-testid={`set-paused-${c.id}`}>Set paused</button>}
            {status !== "archived" && <button onClick={() => { onSetStatus("archived"); setOpen(false); }} className="block w-full text-left px-4 py-2.5 text-xs hover:text-[var(--t-error)]" data-testid={`set-archived-${c.id}`}>Archive</button>}
          </div>
        </>
      )}
    </div>
  );
};

const InlineTypePicker = ({ client, onSetServiceTier }) => {
  const options = OFFERING_KEYS.map((key) => [key, OFFERING_LABEL[key]]);
  const labelByValue = useMemo(() => Object.fromEntries(options), []);
  const currentLabel = labelByValue[canonicalOffering(client.service_tier)] || client.service_tier;
  return (
    <div className="relative inline-block">
      <div data-testid={`inline-type-picker-${client.id}`}>
        <div className="text-xs leading-snug whitespace-normal break-words text-[var(--t-accent)]">
          {(currentLabel || "").toUpperCase()}
        </div>
      </div>
      <select
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        value={client.service_tier}
        onChange={(e) => onSetServiceTier(client, e.target.value)}
        onClick={(e) => e.stopPropagation()}
        data-testid={`inline-type-select-${client.id}`}
        aria-label="Engagement type"
      >
        {options.map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
    </div>
  );
};

const InlineStagePicker = ({ client, onSetStage }) => {
  const options = ENGAGEMENT_STAGE_OPTIONS;
  const current = normalizeEngagementStage(client.engagement_stage);
  return (
    <div className="relative inline-block min-w-[5.5rem]">
      <div data-testid={`inline-stage-picker-${client.id}`}>
        <div className="text-xs leading-snug whitespace-nowrap text-[var(--t-text)]">
          {STAGE_LABEL[current] || current}
        </div>
      </div>
      <select
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        value={current}
        onChange={(e) => onSetStage(client, e.target.value)}
        onClick={(e) => e.stopPropagation()}
        data-testid={`inline-stage-select-${client.id}`}
        aria-label="Engagement stage"
      >
        {options.map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
    </div>
  );
};

const InlineMonthlyToggle = ({ client, onSetEngagement }) => {
  const checked = client.engagement_type === "monthly";
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid={`inline-monthly-${client.id}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onSetEngagement(client, e.target.checked ? "monthly" : "project")}
        className="accent-[var(--t-accent)]"
        aria-label="Monthly client"
      />
      <span className="t-meta text-xs">{checked ? "Yes" : "No"}</span>
    </label>
  );
};

function BrandPreviewWithTrash({ src, imgClassName, busy, onTrashClick }) {
  return (
    <div className="relative shrink-0 inline-flex rounded-[var(--t-event-pill-radius)] overflow-hidden group">
      <img src={src} alt="" className={imgClassName} />
      <button
        type="button"
        disabled={busy}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onTrashClick();
        }}
        className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none"
        aria-label="Remove image"
      >
        <Trash2 size={20} strokeWidth={1.5} className="text-white drop-shadow" aria-hidden />
      </button>
    </div>
  );
}

async function prepareBrandUpload(file) {
  if (!file || file.type === "image/svg+xml") return file;
  if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) return file;

  const maxEdge = 1600;
  const targetBytes = 1.5 * 1024 * 1024;
  if (file.size <= targetBytes) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const outputType = file.type === "image/png" ? "image/png" : "image/webp";
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => (result ? resolve(result) : reject(new Error("Could not encode image"))),
        outputType,
        outputType === "image/png" ? undefined : 0.85,
      );
    });
    const baseName = (file.name || "upload").replace(/\.[^.]+$/, "");
    const ext = outputType === "image/png" ? "png" : "webp";
    return new File([blob], `${baseName}.${ext}`, { type: outputType, lastModified: Date.now() });
  } catch {
    return file;
  }
}

const CreateClientModal = ({ client, onClose, onCreated, onClientUpdated }) => {
  const isEdit = Boolean(client?.id);
  const reduce = useReducedMotion();
  const [form, setForm] = useState({
    name: client?.name || "",
    contact_email: client?.primary_contact_email || "",
    contact_name: client?.primary_contact_name || "",
    timeline_start: isoToFormDate(client?.timeline_start),
    timeline_end: isoToFormDate(client?.timeline_end),
    service_tier: client?.service_tier || "web_design",
    engagement_type: client?.engagement_type || "project",
    rate: client?.rate ?? "",
    payment_structure: client?.payment_structure || "One-time",
    send_invite: true,
  });
  const [timelineOpen, setTimelineOpen] = useState(false);
  const timelineRange = useMemo(() => ({
    from: parseDateValue(form.timeline_start),
    to: parseDateValue(form.timeline_end),
  }), [form.timeline_start, form.timeline_end]);
  const [timelineDraft, setTimelineDraft] = useState({ from: undefined, to: undefined });

  useEffect(() => {
    if (!timelineOpen) return;
    setTimelineDraft({
      from: parseDateValue(form.timeline_start),
      to: parseDateValue(form.timeline_end),
    });
  }, [timelineOpen, form.timeline_start, form.timeline_end]);
  const [clientIconFile, setClientIconFile] = useState(null);
  const [companyLogoFile, setCompanyLogoFile] = useState(null);
  const [iconBlobUrl, setIconBlobUrl] = useState(null);
  const [companyLogoBlobUrl, setCompanyLogoBlobUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [brandUploadBusy, setBrandUploadBusy] = useState(null);
  const clientIconInputRef = useRef(null);
  const companyLogoInputRef = useRef(null);
  const [removeBrandKind, setRemoveBrandKind] = useState(null);
  const formLocked = busy || brandUploadBusy !== null;

  useEffect(() => {
    if (!clientIconFile) {
      setIconBlobUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(clientIconFile);
    setIconBlobUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [clientIconFile]);

  useEffect(() => {
    if (!companyLogoFile) {
      setCompanyLogoBlobUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(companyLogoFile);
    setCompanyLogoBlobUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [companyLogoFile]);

  const iconPreviewSrc = iconBlobUrl || (client?.logo_url ? toAbsoluteAssetUrl(client.logo_url) : null);
  const companyLogoPreviewSrc = companyLogoBlobUrl || (client?.company_logo_url ? toAbsoluteAssetUrl(client.company_logo_url) : null);

  const refreshClient = async () => {
    if (typeof onCreated === "function") await onCreated();
  };

  const uploadBrandImage = async (kind, file) => {
    if (!client?.id || !file) return;
    setBrandUploadBusy(kind);
    try {
      const prepared = await prepareBrandUpload(file);
      const path = kind === "icon"
        ? `/admin/clients/${client.id}/logo`
        : `/admin/clients/${client.id}/company-logo`;
      const fd = new FormData();
      fd.append("file", prepared);
      const result = await postMultipart(path, fd);
      if (kind === "icon") {
        setClientIconFile(null);
        if (clientIconInputRef.current) clientIconInputRef.current.value = "";
      } else {
        setCompanyLogoFile(null);
        if (companyLogoInputRef.current) companyLogoInputRef.current.value = "";
      }
      const updated = result?.client || {
        ...client,
        ...(kind === "icon"
          ? { logo_url: result?.logo_url ?? client.logo_url }
          : { company_logo_url: result?.company_logo_url ?? client.company_logo_url }),
      };
      onClientUpdated?.(updated);
      toast.success(kind === "icon" ? "Client icon updated" : "Company logo updated");
      void refreshClient();
    } catch (err) {
      toast.error(err.response?.data?.detail || (kind === "icon" ? "Could not upload client icon" : "Could not upload company logo"));
    } finally {
      setBrandUploadBusy(null);
    }
  };

  const handleClientIconChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setClientIconFile(null);
      return;
    }
    if (isEdit && client?.id) {
      setClientIconFile(file);
      void uploadBrandImage("icon", file);
      return;
    }
    setClientIconFile(file);
  };

  const handleCompanyLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setCompanyLogoFile(null);
      return;
    }
    if (isEdit && client?.id) {
      setCompanyLogoFile(file);
      void uploadBrandImage("company", file);
      return;
    }
    setCompanyLogoFile(file);
  };

  const executeRemoveBrandImage = async () => {
    const kind = removeBrandKind;
    setRemoveBrandKind(null);
    if (!kind) return;
    if (kind === "icon" && clientIconFile) {
      setClientIconFile(null);
      if (clientIconInputRef.current) clientIconInputRef.current.value = "";
      return;
    }
    if (kind === "company" && companyLogoFile) {
      setCompanyLogoFile(null);
      if (companyLogoInputRef.current) companyLogoInputRef.current.value = "";
      return;
    }
    if (!client?.id) return;
    setBusy(true);
    try {
      const { data } = kind === "icon"
        ? await api.delete(`/admin/clients/${client.id}/logo`)
        : await api.delete(`/admin/clients/${client.id}/company-logo`);
      if (data?.client) onClientUpdated?.(data.client);
      toast.success(kind === "icon" ? "Client icon removed" : "Company logo removed");
      void refreshClient();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove image");
    } finally {
      setBusy(false);
    }
  };

  const persist = async () => {
    if (formLocked) return false;
    setBusy(true);
    try {
      let clientId = client?.id || null;
      if (!isEdit) {
        const created = await api.post("/admin/clients", {
          ...form,
          kind: "engagement",
          engagement_stage: "active",
          rate: form.rate === "" ? null : Number(form.rate),
          send_invite: Boolean(form.send_invite),
          timeline_start: toTimelinePayload(form.timeline_start),
          timeline_end: toTimelinePayload(form.timeline_end),
        });
        clientId = created?.data?.client?.id;
      } else {
        const initialTimelineStart = isoToFormDate(client?.timeline_start);
        const initialTimelineEnd = isoToFormDate(client?.timeline_end);
        const updates = [
          api.patch(`/admin/clients/${clientId}`, {
            name: form.name,
            contact_name: form.contact_name,
            contact_email: form.contact_email,
            rate: form.rate === "" ? null : Number(form.rate),
            payment_structure: form.payment_structure,
          }),
        ];
        if (form.timeline_start !== initialTimelineStart || form.timeline_end !== initialTimelineEnd) {
          updates.push(api.patch(`/admin/clients/${clientId}/timeline-range`, {
            timeline_start: toTimelinePayload(form.timeline_start),
            timeline_end: toTimelinePayload(form.timeline_end),
          }));
        }
        if (form.engagement_type !== client?.engagement_type) {
          updates.push(api.patch(`/admin/clients/${clientId}/engagement-type`, { engagement_type: form.engagement_type }));
        }
        if (form.service_tier !== client?.service_tier) {
          updates.push(api.patch(`/admin/clients/${clientId}/service-tier`, { service_tier: form.service_tier }));
        }
        await Promise.all(updates);
        await api.post(`/admin/clients/${clientId}/notion-sync`);
      }

      const uploads = [];
      if (clientIconFile && clientId) {
        const preparedIcon = await prepareBrandUpload(clientIconFile);
        const fd = new FormData();
        fd.append("file", preparedIcon);
        uploads.push(postMultipart(`/admin/clients/${clientId}/logo`, fd));
      }
      if (companyLogoFile && clientId) {
        const preparedLogo = await prepareBrandUpload(companyLogoFile);
        const fd = new FormData();
        fd.append("file", preparedLogo);
        uploads.push(postMultipart(`/admin/clients/${clientId}/company-logo`, fd));
      }
      if (uploads.length) await Promise.all(uploads);

      toast.success(isEdit ? "Engagement updated" : "Engagement created.");
      void refreshClient();
      onClose();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? "Could not update engagement" : "Could not create engagement"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    await persist();
  };

  /** Edit: backdrop / X saves. Create: discard. Cancel always discards. */
  const dismiss = () => {
    if (formLocked) return;
    if (!isEdit) {
      onClose();
      return;
    }
    if (!(form.name || "").trim() || !(form.contact_name || "").trim()) {
      onClose();
      return;
    }
    void persist();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/25 p-0 sm:p-4"
      onClick={dismiss}
      data-testid="create-client-modal"
    >
      <motion.aside
        initial={reduce ? false : { x: 28, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.25 }}
        className="neo-panel neo-drawer flex h-full w-full max-w-md flex-col overflow-hidden rounded-none sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--t-border)] px-6 py-5">
          <div className="min-w-0">
            <div className="label-eyebrow mb-2">{isEdit ? "Edit engagement" : "Engagements"}</div>
            <div className="font-heading truncate text-2xl tracking-tight !font-extralight">
              {isEdit ? form.name || "Untitled" : "New engagement"}
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-xl p-2 text-[var(--t-meta)] hover:bg-black/[0.04] hover:text-[var(--t-text)]"
            aria-label="Close"
            disabled={formLocked}
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
          <div className="t-scroll-soft flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div>
              <label className="label-eyebrow mb-2 block">Client name</label>
              <input
                className="t-input !rounded-xl !bg-white"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="form-client-name"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label-eyebrow mb-2 block">Contact name</label>
                <input
                  className="t-input !rounded-xl !bg-white"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  required
                  data-testid="form-contact-name"
                />
              </div>
              <div>
                <label className="label-eyebrow mb-2 block">Contact email</label>
                <input
                  type="email"
                  className="t-input !rounded-xl !bg-white"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  required
                  data-testid="form-contact-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow mb-2 block">Type</label>
                <div className="relative">
                  <select
                    className="t-select h-[45px] w-full appearance-none !rounded-xl !bg-white !px-4 !pr-10"
                    value={form.service_tier}
                    onChange={(e) => setForm({ ...form, service_tier: e.target.value })}
                    data-testid="form-type"
                  >
                    <option value="brand_design">Brand design</option>
                    <option value="web_design">Web design</option>
                    <option value="product_design">Product design</option>
                    <option value="operational_ecosystem">Operational ecosystem</option>
                  </select>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.6}
                    aria-hidden
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-meta)]"
                  />
                </div>
              </div>
              <div>
                <label className="label-eyebrow mb-2 block">Engagement</label>
                <div className="relative">
                  <select
                    className="t-select h-[45px] w-full appearance-none !rounded-xl !bg-white !px-4 !pr-10"
                    value={form.engagement_type}
                    onChange={(e) => setForm({ ...form, engagement_type: e.target.value })}
                    data-testid="form-engagement-type"
                  >
                    <option value="project">Project</option>
                    <option value="monthly">Monthly</option>
                  </select>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.6}
                    aria-hidden
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-meta)]"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label-eyebrow mb-2 block">Rate (USD)</label>
                <div className="t-input flex items-center gap-2 !rounded-xl !bg-white px-4">
                  <span className="shrink-0 text-sm text-[var(--t-meta)]">$</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[14px] font-light text-[var(--t-text)] outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    value={form.rate}
                    onChange={(e) => setForm({ ...form, rate: e.target.value })}
                    placeholder="3500"
                    data-testid="form-rate"
                  />
                </div>
              </div>
              <div>
                <label className="label-eyebrow mb-2 block">Payment structure</label>
                <div className="relative">
                  <select
                    className="t-select h-[45px] w-full appearance-none !rounded-xl !bg-white !px-4 !pr-10"
                    value={form.payment_structure}
                    onChange={(e) => setForm({ ...form, payment_structure: e.target.value })}
                    data-testid="form-payment-structure"
                  >
                    <option value="One-time">One-time</option>
                    <option value="Split Payments">Split Payments</option>
                  </select>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.6}
                    aria-hidden
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--t-meta)]"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="label-eyebrow mb-2 block">Timeline</label>
              <button
                type="button"
                aria-expanded={timelineOpen}
                onClick={() => setTimelineOpen((open) => !open)}
                className="t-btn-secondary w-full min-h-[44px] justify-between !rounded-xl px-4"
                data-testid="form-timeline-range"
              >
                <span className="truncate">
                  {timelineRange.from
                    ? `${timelineRange.from.toLocaleDateString(undefined, { month: "short", day: "numeric" })}${timelineRange.to ? ` – ${timelineRange.to.toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}`
                    : "Select timeline"}
                </span>
              </button>
              {timelineOpen ? (
                <div className={MODAL_TIMELINE_PANEL_CLASS} data-testid="form-timeline-panel">
                  <div className="flex items-center justify-between gap-3 border-b border-[var(--t-border)] p-3">
                    <div className="label-eyebrow">Timeline</div>
                    <button
                      type="button"
                      className="t-btn-ghost px-2 py-1 text-xs"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, timeline_start: "", timeline_end: "" }));
                        setTimelineDraft({ from: undefined, to: undefined });
                      }}
                      data-testid="form-timeline-clear"
                    >
                      Clear
                    </button>
                  </div>
                  <Calendar
                    mode="range"
                    selected={timelineDraft}
                    onSelect={(next) => {
                      setTimelineDraft(next || { from: undefined, to: undefined });
                    }}
                    numberOfMonths={1}
                    initialFocus
                    className="w-full min-w-0 p-4"
                  />
                  <div className="flex justify-end border-t border-[var(--t-border)] p-3">
                    <button
                      type="button"
                      className="neo-cta"
                      disabled={!timelineDraft?.from && !timelineDraft?.to}
                      onClick={() => {
                        setForm((prev) => ({
                          ...prev,
                          timeline_start: timelineDraft.from ? toDateValue(timelineDraft.from) : "",
                          timeline_end: timelineDraft.to ? toDateValue(timelineDraft.to) : "",
                        }));
                        setTimelineOpen(false);
                      }}
                      data-testid="form-timeline-apply"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
            {!isEdit ? (
              <label className="flex items-start gap-3 rounded-xl border border-[var(--t-border)] bg-white px-4 py-3">
                <input
                  type="checkbox"
                  className="mt-1 accent-[var(--t-accent)]"
                  checked={Boolean(form.send_invite)}
                  onChange={(e) => setForm({ ...form, send_invite: e.target.checked })}
                  data-testid="form-send-invite"
                />
                <span>
                  <span className="block text-sm text-[var(--t-text)]">Invite to workspace Home</span>
                  <span className="t-meta text-xs">
                    Email a magic link so they can open this engagement’s Home — progress, recent shared files, and the next meeting.
                  </span>
                </span>
              </label>
            ) : null}
            <div>
              <div className="label-eyebrow mb-3">Client icon</div>
              <div className="flex min-w-0 flex-row flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded-xl border border-[var(--t-border)] bg-white px-4 py-3">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                  <label className={`inline-flex ${formLocked ? "pointer-events-none opacity-50" : "cursor-pointer"}`}>
                    <input
                      ref={clientIconInputRef}
                      id="client-icon-file"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      className="sr-only"
                      onChange={handleClientIconChange}
                      disabled={formLocked}
                      data-testid="client-icon-input"
                    />
                    <span className="t-btn-secondary inline-flex min-h-[40px] items-center justify-center !rounded-xl px-4 py-2 text-xs uppercase tracking-wide">
                      {brandUploadBusy === "icon" ? "Uploading…" : iconPreviewSrc ? "Replace file" : "Choose file"}
                    </span>
                  </label>
                </div>
                {iconPreviewSrc ? (
                  <BrandPreviewWithTrash
                    src={iconPreviewSrc}
                    busy={formLocked}
                    imgClassName="h-14 w-14 shrink-0 object-cover border border-[var(--t-border)] bg-[var(--t-bg)]"
                    onTrashClick={() => setRemoveBrandKind("icon")}
                  />
                ) : null}
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-3">Company logo</div>
              <div className="flex min-w-0 flex-row flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded-xl border border-[var(--t-border)] bg-white px-4 py-3">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                  <label className={`inline-flex ${formLocked ? "pointer-events-none opacity-50" : "cursor-pointer"}`}>
                    <input
                      ref={companyLogoInputRef}
                      id="company-logo-file"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                      className="sr-only"
                      onChange={handleCompanyLogoChange}
                      disabled={formLocked}
                      data-testid="company-logo-input"
                    />
                    <span className="t-btn-secondary inline-flex min-h-[40px] items-center justify-center !rounded-xl px-4 py-2 text-xs uppercase tracking-wide">
                      {brandUploadBusy === "company" ? "Uploading…" : companyLogoPreviewSrc ? "Replace file" : "Choose file"}
                    </span>
                  </label>
                </div>
                {companyLogoPreviewSrc ? (
                  <BrandPreviewWithTrash
                    src={companyLogoPreviewSrc}
                    busy={formLocked}
                    imgClassName="h-14 max-w-[min(12rem,42vw)] shrink-0 object-contain object-right border border-[var(--t-border)] bg-[var(--t-bg)]"
                    onTrashClick={() => setRemoveBrandKind("company")}
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-[var(--t-border)] px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="t-btn-secondary !rounded-xl"
              disabled={formLocked}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="neo-cta"
              disabled={formLocked}
              data-testid="form-submit-btn"
            >
              {busy ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save" : "Create"}
            </button>
          </div>
        </form>

        <AlertDialog open={removeBrandKind !== null} onOpenChange={(open) => !open && setRemoveBrandKind(null)}>
          <AlertDialogContent
            className="z-[220] max-w-md gap-5 border border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] shadow-none sm:rounded-2xl"
            data-testid="remove-brand-image-dialog"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-xl font-light tracking-tight text-[var(--t-text)]">
                Remove {removeBrandKind === "company" ? "company logo" : "client icon"}?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm font-light text-[var(--t-meta)]">
                This clears it from the portal for everyone. You can upload a new image afterward.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-3 sm:justify-end">
              <AlertDialogCancel className="t-btn-secondary min-h-[44px] border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] hover:opacity-90">
                Cancel
              </AlertDialogCancel>
              <button
                type="button"
                className="neo-cta"
                data-testid="remove-brand-image-confirm"
                onClick={() => executeRemoveBrandImage()}
              >
                Remove
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </motion.aside>
    </div>
  );
};
